import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Paper,
  Grid,
  Button,
  Snackbar,
} from "@material-ui/core";
import MUIDataTable from "mui-datatables";
import Alert from "@material-ui/lab/Alert";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { connect } from "react-redux";
import swal from "sweetalert";
import axios from "axios";
import moment from "moment";

import { API_URL } from "../../config";
import {
  pathSpliter,
  checkAuthBranchWare,
  dateTimeFormat,
  sortRecordsNewestFirst,
} from "../../lib/functions";
import {
  currentRouteSet,
  createdProdNameSet,
  updatedProdNameSet,
} from "../../actions/actions";

/* ================= STYLES ================= */
const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  pageEntryLabel: {
    color: "#7754cc",
    margin: 0,
    marginBottom: theme.spacing(1),
    textAlign: "center",
  },
  fullWidth: {
    width: "100%",
  },
  button: {
    marginTop: theme.spacing(3),
  },
}));

/* ================= COMPONENT ================= */
const ProdNamesManage = ({
  location,
  authInfo,
  currentRouteSet,
  createdProdName,
  updatedProdName,
  createdProdNameSet,
  updatedProdNameSet,
}) => {
  const classes = useStyles();
  const nameRef = useRef(null);

  const [formValues, setFormValues] = useState({
    prod_name: "",
    prod_name_id: 0,
    action: "create",
  });

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [prodNames, setProdNames] = useState([]);
  const [prodNameUpdateIndex, setProdNameUpdateIndex] = useState(0);

  const [successMsg, setSuccessMsg] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
    msg: "",
  });

  const handleClose = () => {
    setSuccessMsg({ ...successMsg, open: false });
  };

  /* ================= INIT ================= */
  useEffect(() => {
    currentRouteSet(pathSpliter(location.pathname, 1));
    createdProdNameSet(null);
    updatedProdNameSet(null);
    getProdNames();
  }, []);

  /* ================= SOCKET / REDUX UPDATES ================= */
  useEffect(() => {
    if (createdProdName && checkAuthBranchWare(createdProdName.user_branch_id)) {
      setProdNames((prev) => sortRecordsNewestFirst(createdProdName.createdRow.concat(prev)));
      setSuccessMsg({ ...successMsg, msg: createdProdName.msg, open: true });
      resetForm();
    }
  }, [createdProdName]);

  useEffect(() => {
    if (updatedProdName && checkAuthBranchWare(updatedProdName.user_branch_id)) {
      setProdNames((prev) => {
        if (updatedProdName.index < 0 || updatedProdName.index >= prev.length) return prev;
        const next = [...prev];
        next[updatedProdName.index] = updatedProdName.updatedRow[0];
        return next;
      });
      setSuccessMsg({ ...successMsg, msg: updatedProdName.msg, open: true });
      resetForm();
    }
  }, [updatedProdName]);

  /* ================= HANDLERS ================= */
  const resetForm = () => {
    setFormValues({ prod_name: "", prod_name_id: 0, action: "create" });
    if (nameRef.current) nameRef.current.focus();
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const saveFormAction = async () => {
    if (!formValues.prod_name.trim()) {
      swal({ title: "Product name is required", icon: "warning" });
      return;
    }

    try {
      setLoadingSave(true);

      const res = await axios.post(
        `${API_URL}/api/prod-name-cu`,
        {
          ...formValues,
          prod_name: formValues.prod_name.trim(),
          prodNameUpdateIndex,
        },
        { headers: { "auth-token": authInfo.token } }
      );

      if (res?.data?.error) {
        swal({ title: res.data.message || "Save failed", icon: "warning" });
        return;
      }
      await getProdNames();
      resetForm();
    } catch (err) {
      swal({
        title: "Save failed",
        text: err?.response?.data?.message || err?.message || "Request failed",
        icon: "error",
      });
    } finally {
      setLoadingSave(false);
    }
  };

  const prodNameEdit = (index) => {
    setFormValues({
      prod_name: prodNames[index].prod_name,
      prod_name_id: prodNames[index].prod_name_id,
      action: "update",
    });
    setProdNameUpdateIndex(index);
  };

  const getProdNames = async () => {
    try {
      setLoadingList(true);
      const res = await axios.post(
        `${API_URL}/api/get-prod-names`,
        {},
        { headers: { "auth-token": authInfo.token } }
      );
      setProdNames(sortRecordsNewestFirst(res.data.message || []));
    } catch (err) {
      swal({
        title: "Product names load failed",
        text: err?.response?.data?.message || err?.message || "Request failed",
        icon: "error",
      });
    } finally {
      setLoadingList(false);
    }
  };

  const prodNameDisableRestore = async (prodNameId, actionCond, index) => {
    if (actionCond === "disable") {
      const willDelete = await swal({
        title: "Are you sure?",
        text: "Do you want to delete this product name?",
        icon: "warning",
        buttons: ["No", "Yes"],
        dangerMode: true,
      });

      if (!willDelete) return;
    }

    try {
      await axios.post(
        `${API_URL}/api/prod-name-disable-restore`,
        { prod_name_id: prodNameId, action: actionCond, index },
        { headers: { "auth-token": authInfo.token } }
      );

      if (actionCond === "disable") {
        setProdNames((prev) =>
          prev.filter((p) => String(p.prod_name_id) !== String(prodNameId))
        );
      } else {
        setProdNames((prev) =>
          sortRecordsNewestFirst(prev.map((p) =>
            String(p.prod_name_id) === String(prodNameId)
              ? { ...p, prod_name_status: "active" }
              : p
          ))
        );
      }
    } catch (err) {
      swal({
        title: "Action failed",
        text: err?.response?.data?.message || err?.message || "Request failed",
        icon: "error",
      });
    }
  };

  /* ================= TABLE ================= */
  const ActionOptions = ({ rowData, rowIndex }) => (
    <div style={{ textAlign: "right" }}>
      {authInfo.role !== "user" && (
        <>
          <EditIcon
            style={{ cursor: "pointer", color: "#0f7e77", fontSize: 26 }}
            onClick={() => prodNameEdit(rowIndex)}
          />
          <DeleteIcon
            style={{ cursor: "pointer", color: "#ff0202", fontSize: 26, marginLeft: 6 }}
            onClick={() => prodNameDisableRestore(rowData[0], "disable", rowIndex)}
          />
        </>
      )}
    </div>
  );

  const columns = [
    { name: "prod_name_id", options: { display: "excluded" } },
    { name: "prod_name_status", options: { display: "excluded" } },
    { name: "prod_name", label: "Product Name" },
    {
      name: "prod_name_created_isodt",
      label: "Created Date & Time",
      options: {
        customBodyRender: (value) =>
          moment(value).format(dateTimeFormat),
      },
    },
    {
      name: "prod_name_updated_isodt",
      label: "Updated Date & Time",
      options: {
        customBodyRender: (value) =>
          moment(value).format(dateTimeFormat),
      },
    },
    {
      name: "actions",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (_, tableMeta) => (
          <ActionOptions
            rowData={tableMeta.rowData}
            rowIndex={tableMeta.rowIndex}
          />
        ),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    filterType: "checkbox",
  };

  /* ================= RENDER ================= */
  return (
    <div className={classes.root}>
      <Snackbar open={successMsg.open} autoHideDuration={10000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          {successMsg.msg}
        </Alert>
      </Snackbar>

      <Paper className={classes.paper}>
        <h2 className={classes.pageEntryLabel}>Product Name Entry</h2>

        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={4}>
            <TextField
              inputRef={nameRef}
              autoComplete="off"
              className={classes.fullWidth}
              value={formValues.prod_name}
              label="Product name"
              name="prod_name"
              variant="outlined"
              size="small"
              onChange={handleInput}
              onKeyDown={(e) => e.key === "Enter" && saveFormAction()}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.button}
          startIcon={<SaveIcon />}
          disabled={loadingSave}
          onClick={saveFormAction}
        >
          Save
        </Button>
      </Paper>

      {loadingList ? (
        <b>Loading...</b>
      ) : (
        <Paper className={classes.paper}>
          <MUIDataTable
            title="Product Name List"
            data={prodNames}
            columns={columns}
            options={options}
          />
        </Paper>
      )}
    </div>
  );
};

/* ================= REDUX ================= */
const mapStateToProps = (state) => ({
  authInfo: state.authInfoReducer,
  createdProdName: state.createdProdNameReducer,
  updatedProdName: state.updatedProdNameReducer,
});

export default connect(mapStateToProps, {
  currentRouteSet,
  createdProdNameSet,
  updatedProdNameSet,
})(ProdNamesManage);
